import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallEnd01Icon,
  PauseIcon,
  User02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils";
import {
  firstName,
  isPhoneLabel,
  useSoftphone,
  type ActiveTransfer
} from "./SoftphoneProvider";

const pad = (value: number) => String(value).padStart(2, "0");
const clock = (seconds: number) =>
  `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

// Seconds since `since`, ticking once a second while it is set.
const useElapsed = (since: Date | undefined) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [since]);
  return since ? Math.max(0, Math.floor((now - since.getTime()) / 1000)) : null;
};

interface ConsultCopy {
  status: string;
  tone: "muted" | "live" | "error";
  hint: string;
}

const consultCopy = (
  transfer: ActiveTransfer,
  caller: string,
  elapsed: number | null
): ConsultCopy => {
  const first = firstName(transfer.targetName);
  switch (transfer.phase) {
    case "consulting":
      return {
        status: elapsed === null ? "Connected" : clock(elapsed),
        tone: "live",
        hint: `You're talking to ${first}. ${caller} can't hear you.`
      };
    case "consult-not-answered":
      return {
        status: "Didn't answer",
        tone: "error",
        hint: `${first} didn't answer. ${caller} is still on hold.`
      };
    case "consult-destination-left":
      return {
        status: "Hung up",
        tone: "error",
        hint: `${first} hung up. ${caller} is still on hold.`
      };
    default:
      return {
        status: "Ringing…",
        tone: "muted",
        hint: `${caller} hears hold music while ${first}'s phone rings.`
      };
  }
};

/**
 * Talk first (CRM-1747, mockup "Transferring · talk first"): the caller waits
 * on hold while the teammate speaks to the destination, then hands the caller
 * over (Complete transfer) or takes them back (Back to caller). End call ends
 * it for all three.
 */
export function ConsultView({
  transfer,
  callerName
}: {
  transfer: ActiveTransfer;
  callerName: string;
}) {
  const { consultAction, hangup } = useSoftphone();
  const [pending, setPending] = useState<"complete" | "back" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const elapsed = useElapsed(
    transfer.phase === "consulting" ? transfer.consultAnsweredAt : undefined
  );
  const copy = consultCopy(transfer, callerName, elapsed);
  const canComplete = transfer.phase === "consulting";

  const run = async (action: "complete" | "back") => {
    setPending(action);
    setError(null);
    try {
      await consultAction(action);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="w-full text-left">
      <p className="text-xs font-medium uppercase tracking-wide text-content-300">
        Transferring · talk first
      </p>

      <div className="mt-4 space-y-2">
        <PartyRow
          name={callerName}
          sub="Caller"
          status={
            <span className="inline-flex items-center gap-1 text-content-400">
              <HugeiconsIcon icon={PauseIcon} size={14} />
              On hold
            </span>
          }
        />
        <PartyRow
          name={transfer.targetName}
          sub="Transfer to"
          status={
            <span
              className={cx(
                "tabular-nums",
                copy.tone === "live" && "text-success-500",
                copy.tone === "error" && "text-danger-500",
                copy.tone === "muted" && "text-content-400"
              )}
            >
              {copy.status}
            </span>
          }
        />
      </div>

      <p className="mt-3 text-xs text-content-400">{copy.hint}</p>
      {error ? <p className="mt-2 text-xs text-danger-500">{error}</p> : null}

      <div className="mt-5 space-y-2">
        {canComplete ? (
          <Button
            className="w-full"
            size={ButtonSize.BASE}
            loading={pending === "complete"}
            disabled={pending !== null}
            onClick={() => void run("complete")}
          >
            Complete transfer
          </Button>
        ) : null}
        <Button
          className="w-full"
          size={ButtonSize.BASE}
          variant={canComplete ? ButtonVariant.BASE : ButtonVariant.PRIMARY}
          loading={pending === "back"}
          disabled={pending !== null}
          onClick={() => void run("back")}
        >
          Back to caller
        </Button>
      </div>

      <div className="mt-5 flex justify-center">
        <button
          type="button"
          onClick={hangup}
          className="flex flex-col items-center gap-2"
        >
          <span className="grid size-12 place-items-center rounded-full bg-danger-500 text-white transition hover:bg-danger-600">
            <HugeiconsIcon icon={CallEnd01Icon} size={22} />
          </span>
          <span className="text-xs text-content-400">End call</span>
        </button>
      </div>
    </div>
  );
}

function PartyRow({
  name,
  sub,
  status
}: {
  name: string;
  sub: string;
  status: React.ReactNode;
}) {
  // A number has no initials to show.
  const isNumber = isPhoneLabel(name);
  return (
    <div className="flex items-center gap-3 rounded-xl bg-alpha-40 p-3">
      <Avatar
        name={isNumber ? undefined : name}
        size={36}
        fallbackText={<HugeiconsIcon icon={User02Icon} size={18} />}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-content-1000">
          {name}
        </p>
        <p className="truncate text-xs text-content-400">{sub}</p>
      </div>
      <span className="shrink-0 text-xs font-medium">{status}</span>
    </div>
  );
}
